import { useMemo, useState, type ReactNode } from "react";
import type { TrainingEvalRunSummaryView } from "@/api/trainingEval";
import { RightSidePanel } from "@/components/modal";
import { EvalRunDetailPanel } from "../EvalRunDetailPanel";
import { EvaluationComparisonViewer } from "./EvaluationComparisonViewer";
import { flattenCompareGroups, type EvalCompareGroup } from "./policyCompareGroups";
import { useEvalCompareController } from "./useEvalCompareController";

// 정책 비교 side panel 이다. EvaluationPage 가 로컬 상태로 조건부 렌더한다.
// 정책별 run 구성은 조회 시점의 스냅샷이다. 그래서 페이지 세션 안에서만 유지한다.
// Results 창의 비교·병합은 그 창의 로컬 상태가 따로 소유한다.
// 마운트는 열림을 뜻한다. 닫아서 언마운트하면 로드한 비교 데이터도 함께 버린다.
export function PolicyComparePanel({
  groups,
  onClose,
}: {
  groups: readonly EvalCompareGroup[];
  onClose: () => void;
}): ReactNode {
  // 그룹을 평평한 run 목록으로 바꿔 비교·병합과 같은 controller 에 전달한다. run 색 슬롯은
  // 그룹 순번과 같다. 그래서 run 색을 어디에 쓰든 그룹 시리즈 색과 같다. 정책 풀링은 에피소드
  // 병합 계산만 하므로 metrics 를 받지 않는다.
  const { runs, slotById } = useMemo(() => flattenCompareGroups(groups), [groups]);
  const controller = useEvalCompareController(runs, slotById, true, false);
  // 상세는 이 패널에 속한 드릴다운이라 이 컴포넌트가 소유한다. EvalComparePanel 과 같은 계약이다.
  const [detailRun, setDetailRun] = useState<TrainingEvalRunSummaryView | null>(null);

  return (
    <>
      <RightSidePanel
        title={`Policy Evaluation 비교 · Policy ${groups.length}개`}
        width={{ kind: "flex", percent: 80 }}
        onBackdrop={onClose}
        onRequestClose={onClose}
      >
        <EvaluationComparisonViewer
          controller={controller}
          mode="group"
          groups={groups}
          onOpenRunDetail={setDetailRun}
        />
      </RightSidePanel>

      {/* run id 를 key 로 준다. 다른 열을 눌러 run 을 바꾸면 콘텐츠를 새로 만든다. 그래서 내부
          상태가 이전 run 의 값으로 남지 않는다. 이 패널보다 나중에 마운트되므로 등록 순서만으로
          위에 겹친다. */}
      {detailRun !== null ? (
        <EvalRunDetailPanel key={detailRun.id} run={detailRun} onClose={() => setDetailRun(null)} />
      ) : null}
    </>
  );
}
